import { QueryClient } from '@tanstack/react-query';
import { marketsApi } from '../services/api/markets';

/**
 * Prefetch all continents and their countries into React Query cache
 * Called after successful authentication to improve navigation performance
 *
 * This runs in the background without blocking the UI:
 * 1. Prefetch continents list
 * 2. Prefetch countries for each continent in parallel
 */
export const prefetchMarketData = async (queryClient: QueryClient): Promise<void> => {
  try {
    // Step 1: Prefetch continents (required to get continent IDs)
    await queryClient.prefetchQuery({
      queryKey: ['continents'],
      queryFn: () => marketsApi.getContinents(),
      staleTime: 30 * 24 * 60 * 60 * 1000, // 30 days
    });

    // Get the cached continents data to extract continent IDs
    const cachedData = queryClient.getQueryData(['continents']) as any;
    const continents = cachedData?.continents || [];

    // Step 2: Prefetch countries for each continent in parallel
    if (continents.length > 0) {
      const countryPrefetchPromises = continents.map((continent: any) =>
        queryClient.prefetchQuery({
          queryKey: ['countries', continent.id],
          queryFn: () => marketsApi.getCountries(continent.id),
          staleTime: 30 * 24 * 60 * 60 * 1000, // 30 days
        })
      );

      // Wait for all country prefetch operations to complete
      await Promise.all(countryPrefetchPromises);
    }

    console.log(`Successfully prefetched ${continents.length} continents and their countries`);
  } catch (error) {
    // Log error but don't throw - prefetching failures shouldn't break the app
    console.error('Error prefetching market data:', error);
  }
};
